import { useReportingStore } from "../../../../../stores/ReportingStore";
import LoaderStats from "../../LoaderStats";
import {
  ActionIcon,
  Checkbox,
  Group,
  Menu,
  Paper,
  RingProgress,
  Text,
} from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { IconCheck, IconDots } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { getReportingRateColor } from "../../../../../functions/utilFunctions";
import ReportingInfo, { ReportingScaleText } from "../../ReportingInfo";
import { useLanguage } from "../../../../../stores/LanguageStore";

const PerfRate = () => {
  const lang = useLanguage((s) => s.language);
  const dateRange = useReportingStore((s) => s.dateRange);
  const globalStats = useReportingStore((s) => s.actualStats);
  const actualStatsDate = useReportingStore((s) => s.actualStatsDate);

  const statistics = useMemo(() => {
    if (!dateRange.start_date && !dateRange.end_date) {
      return globalStats;
    } else {
      const totals = actualStatsDate.reduce((acc, curr) => {
        Object.keys(curr).forEach((key) => {
          if (key !== "date") {
            acc[key] = (acc[key] || 0) + curr[key];
          }
        });
        return acc;
      }, {});

      return {
        opener_total: totals.opener,
        clicker_total: totals.clicker,
        answerer_total: totals.answerer,
        contact_email_bounce: totals.bounce,
        ...totals,
      };
    }
  }, [actualStatsDate, dateRange.end_date, dateRange.start_date, globalStats]);

  const [isGlobal, setIsGlobal] = useState(true);
  const [noCLicker, setNoClicker] = useState(false);

  const openPerf = useMemo(() => (noCLicker ? 0.3 : 0.25), [noCLicker]);
  const clickPerf = 0.35;
  const answerPerf = useMemo(() => (noCLicker ? 0.7 : 0.4), [noCLicker]);

  const openersRate = useMemo(() => {
    if (statistics.sent) {
      return (statistics.opener_total / statistics.sent) * 100;
    }
    return 0;
  }, [statistics.opener_total, statistics.sent]);

  const clickersRate = useMemo(() => {
    if (isGlobal) {
      if (statistics.opener_total) {
        return (statistics.clicker_total / statistics.opener_total) * 100;
      }
      return 0;
    } else {
      if (statistics.sent) {
        return (statistics.clicker_total / statistics.sent) * 100;
      }
      return 0;
    }
  }, [
    isGlobal,
    statistics.clicker_total,
    statistics.opener_total,
    statistics.sent,
  ]);

  const answerersRate = useMemo(() => {
    if (isGlobal) {
      if (statistics.opener_total) {
        return (statistics.answerer_total / statistics.opener_total) * 100;
      }
      return 0;
    } else {
      if (statistics.sent) {
        return (statistics.answerer_total / statistics.sent) * 100;
      }
      return 0;
    }
  }, [
    isGlobal,
    statistics.answerer_total,
    statistics.opener_total,
    statistics.sent,
  ]);

  const perfRate = useMemo(() => {
    return (
      (openersRate * openPerf +
        clickersRate * clickPerf +
        answerersRate * answerPerf) /
      (openPerf + clickPerf + answerPerf)
    );
  }, [answerPerf, answerersRate, clickersRate, openPerf, openersRate]);

  const data = useMemo(() => {
    if (noCLicker) {
      return [
        {
          type: lang.statistics.contacts_type.opener,
          rate: Math.round(openersRate * 10) / 10,
        },
        {
          type: lang.statistics.contacts_type.answerer,
          rate: Math.round(answerersRate * 10) / 10,
        },
      ];
    }
    return [
      {
        type: lang.statistics.contacts_type.opener,
        rate: Math.round(openersRate * 10) / 10,
      },
      {
        type: lang.statistics.contacts_type.clicker,
        rate: Math.round(clickersRate * 10) / 10,
      },
      {
        type: lang.statistics.contacts_type.answerer,
        rate: Math.round(answerersRate * 10) / 10,
      },
    ];
  }, [answerersRate, clickersRate, noCLicker, openersRate, lang]);

  return (
    <Paper pos={"relative"} flex={1} p={"xl"} pt={"md"}>
      <LoaderStats isDate />

      <Group mb={32} justify="center">
        <Text size="lg" fw={600}>
          {lang.reports.perf_title}
        </Text>

        <ReportingInfo>
          <Text>{lang.reports.perf_title_text}</Text>
        </ReportingInfo>

        <Menu>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              size={"xl"}
              pos={"absolute"}
              top={8}
              right={8}
            >
              <IconDots size={32} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item onClick={() => setIsGlobal(!isGlobal)}>
              <Group justify="end" gap={"xs"}>
                {isGlobal && <IconCheck />}
                <Text size="md">{lang.reports.base_engaged}</Text>
              </Group>
            </Menu.Item>
            <Menu.Item onClick={() => setIsGlobal(!isGlobal)}>
              <Group justify="end" gap={"xs"}>
                {!isGlobal && <IconCheck />}
                <Text size="md">{lang.reports.base_sent}</Text>
              </Group>
            </Menu.Item>

            <Menu.Divider mb={8} />

            <Checkbox
              ml={12}
              my={4}
              checked={!noCLicker}
              onClick={() => setNoClicker(!noCLicker)}
              label={lang.statistics.contacts_type.clicker}
              size="md"
            />
          </Menu.Dropdown>
        </Menu>
      </Group>

      <LineChart
        h={200}
        w={"100%"}
        data={data}
        dataKey="type"
        series={[{ name: "rate", label: lang.reports.rate, color: "indigo.6" }]}
        tickLine="y"
        unit="%"
        dotProps={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
        activeDotProps={{ r: 8, strokeWidth: 1, fill: "#fff" }}
        xAxisProps={{ padding: { left: 32, right: 32 } }}
        lineProps={{ isAnimationActive: true, animationDuration: 600 }}
      />

      <Group justify="end" wrap="nowrap" mt={28}>
        <ReportingInfo>
          <Text mb={4}>{lang.reports.perf_index_text}</Text>
          <ReportingScaleText />
        </ReportingInfo>
        <Text size="lg" ml={-8}>
          {lang.reports.perf_index}
        </Text>
        <RingProgress
          label={
            <Group justify="center" ml={12} mt={-8}>
              <Text
                c={getReportingRateColor(perfRate)}
                fz={30}
                ta={"center"}
                fw={600}
              >
                {Math.floor(perfRate)}
              </Text>
            </Group>
          }
          size={96}
          roundCaps={perfRate < 92}
          thickness={10}
          sections={[{ value: 100, color: getReportingRateColor(perfRate) }]}
        />
      </Group>
    </Paper>
  );
};

export default PerfRate;
